/**
 * Connect hub SSO handoff (IMP-2065).
 *
 * A user signed in here opens the Connect hub without a second login. Connect
 * has no session of its own: it verifies this app's JWT with the shared
 * LEGACY_JWT_KEY, so our token is already valid currency there. The work is
 * placing that token into Connect's storage before its auth guard runs.
 *
 * The token travels in the URL fragment (#token=...), never a ?query param.
 * Browsers do not send fragments to servers, so the token stays out of access
 * logs and the Referer header.
 *
 * The base URL is validated before the token is appended. safeWindowOpen logs
 * a rejected URL with console.warn, so appending first would print the token to
 * the console whenever REACT_APP_CONNECT_HUB_URL is missing.
 */
import { getAccessToken } from './index';
import { isSafeExternalUrl, safeWindowOpen } from './safeNavigation';
import { getUtmSource } from './campaignAttribution';
import { ROLE } from '../static';

const { MENTOR, MENTEE } = ROLE;

/** Roles that have a pair view in the Connect hub. */
const HANDOFF_ROLES = [MENTOR, MENTEE];

/** Configured per environment. Absent in local builds until the var is set. */
export const getConnectHubBaseUrl = () => process.env.REACT_APP_CONNECT_HUB_URL;

/**
 * True when the current session may open the Connect hub.
 *
 * This hides a dead-end click. It is not the security control: Connect's
 * verifyLegacyToken checks the signature and role and is the real gate.
 *
 * An admin LoginAs session is allowed. Its token is minted for the member
 * (their _id, email and role), so Connect opens as that member and the admin
 * can reproduce what the member sees. `role` is the member's role in that case.
 *
 * @param {string} role - `userData.role` from redux, or the cookie payload.
 * @returns {boolean}
 */
export function canHandOffToConnectHub(role) {
  if (!isSafeExternalUrl(getConnectHubBaseUrl())) return false;
  return HANDOFF_ROLES.includes(role);
}

/**
 * Builds the handoff URL, or returns null when the session cannot hand off.
 *
 * Query params (utm_source, community_id) are attached ahead of the
 * `#token=` fragment so Connect hub's GA4 can read them off its landing URL.
 * They are separate from the fragment and do not change where the token
 * travels (see the file header).
 *
 * @param {string} [token] - Defaults to the current session token.
 * @param {object} [options]
 * @param {string} [options.communityId] - Omitted from the URL when empty.
 * @param {string} [options.utmSource] - Defaults to the persisted campaign value.
 * @returns {string | null}
 */
export function buildConnectHubUrl(token = getAccessToken(), { communityId, utmSource = getUtmSource() } = {}) {
  const base = getConnectHubBaseUrl();
  if (!isSafeExternalUrl(base)) return null;
  if (typeof token !== 'string' || !token) return null;

  // URLSearchParams percent-encodes each value, so a crafted community id or
  // utm_source cannot inject an extra param or move the `#token=` fragment.
  const params = new URLSearchParams();
  if (utmSource) params.set('utm_source', utmSource);
  if (communityId) params.set('community_id', communityId);
  const query = params.toString();

  return `${base.replace(/\/+$/, '')}/${query ? `?${query}` : ''}#token=${encodeURIComponent(token)}`;
}

/**
 * Opens the Connect hub in a new tab.
 *
 * @param {string} [token] - Defaults to the current session token.
 * @param {object} [options] - Forwarded to buildConnectHubUrl.
 * @returns {boolean} True when the tab opened.
 */
export function openConnectHub(token = getAccessToken(), options) {
  const url = buildConnectHubUrl(token, options);
  if (url === null) return false;
  return safeWindowOpen(url) !== null;
}
